import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import OfficeMap from "./OfficeMap";
import { z } from "zod";
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';

// Disposable email domains to block
const DISPOSABLE_DOMAINS = [
  'tempmail.com', 'throwaway.email', '10minutemail.com', 'guerrillamail.com',
  'mailinator.com', 'trashmail.com', 'temp-mail.org', 'maildrop.cc',
  'fakeinbox.com', 'tempail.com', 'dispostable.com', 'yopmail.com'
];

const isDisposableEmail = (email: string): boolean => {
  const domain = email.split('@')[1]?.toLowerCase();
  return DISPOSABLE_DOMAINS.includes(domain);
};

// UK phone number validation
const isValidUKPhone = (phone: string): boolean => {
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  // UK phone: starts with +44 or 0, followed by 10-11 digits total
  return /^(\+44|0)[1-9]\d{8,10}$/.test(cleaned);
};

const contactSchema = z.object({
  name: z.string()
    .trim()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters")
    .refine((val) => /^[a-zA-Z\s\-'.]+$/.test(val), "Name contains invalid characters"),
  email: z.string()
    .trim()
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters")
    .refine((val) => !isDisposableEmail(val), "Disposable email addresses are not allowed"),
  phone: z.string()
    .trim()
    .min(1, "Phone number is required")
    .refine((val) => isValidUKPhone(val), "Please enter a valid UK phone number (e.g., 07123 456789 or +44 7123 456789)"),
  company: z.string()
    .trim()
    .max(200, "Company name must be less than 200 characters")
    .optional()
    .or(z.literal('')),
  message: z.string()
    .trim()
    .min(10, "Message must be at least 10 characters")
    .max(2000, "Message must be less than 2000 characters"),
  preferredContact: z.enum(["email", "phone"]).optional(),
  honeypot: z.string().max(0, "Bot detection triggered").optional(),
  formTimestamp: z.number(),
  consent: z.boolean().refine((val) => val === true, {
    message: "You must consent to being contacted",
  }),
  turnstileToken: z.string().min(1, "Please confirm you're human"),
});

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const { toast } = useToast();
  const [formTimestamp] = useState(() => Date.now());
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    preferredContact: "" as "" | "email" | "phone",
    honeypot: "",
  });
  const [consent, setConsent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<TurnstileInstance>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Calculate form completion time in seconds
      const timeTaken = (Date.now() - formTimestamp) / 1000;
      
      // Validate input
      const validatedData = contactSchema.parse({
        ...formData,
        preferredContact: formData.preferredContact || undefined,
        formTimestamp: timeTaken,
        consent,
        turnstileToken,
      });

      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          name: validatedData.name,
          email: validatedData.email,
          phone: validatedData.phone,
          company: validatedData.company || null,
          message: validatedData.message,
          preferredContact: validatedData.preferredContact || null,
          honeypot: validatedData.honeypot,
          formTimestamp: validatedData.formTimestamp,
          turnstileToken: validatedData.turnstileToken,
        },
      });

      if (error) throw error;

      // Check for rate limiting or other errors
      if (data?.error) {
        toast({
          title: "Unable to submit",
          description: data.error,
          variant: "destructive",
        });
        // Reset turnstile on error
        turnstileRef.current?.reset();
        setTurnstileToken("");
        return;
      }

      // Show success message
      setFormSubmitted(true);
      toast({
        title: "Thanks!",
        description: "Check your email to confirm your enquiry.",
      });

      // Reset form
      setFormData({ name: "", email: "", phone: "", company: "", message: "", preferredContact: "", honeypot: "" });
      setConsent(false);
      setTurnstileToken("");
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });
      }
      // Reset turnstile on error
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (formSubmitted) {
    return (
      <div className="grid md:grid-cols-2 gap-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-semibold text-primary">Check Your Email</CardTitle>
            <CardDescription>
              We've sent a verification link to your email address
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              To complete your enquiry, please click the verification link in the email we just sent you.
            </p>
            <p className="text-sm text-muted-foreground">
              Can't find it? Check your spam folder, or{" "}
              <button 
                onClick={() => setFormSubmitted(false)} 
                className="text-primary underline hover:no-underline"
              >
                submit again
              </button>.
            </p>
          </CardContent>
        </Card>
        <OfficeMap />
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-semibold">Send us a Message</CardTitle>
          <CardDescription>
            Fill out the form and we'll respond within 24 hours
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Your full name"
                maxLength={100}
              />
            </div>

            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="your@email.com"
                maxLength={255}
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                placeholder="07123 456789 or +44 7123 456789"
                maxLength={20}
              />
              <p className="text-xs text-muted-foreground mt-1">
                UK phone numbers only
              </p>
            </div>

            <div>
              <Label htmlFor="company">Company Name</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Optional"
                maxLength={200}
              />
            </div>

            <div>
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={4}
                placeholder="How can we help you?"
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.message.length}/2000 characters (minimum 10)
              </p>
            </div>

            <div>
              <Label className="mb-2 block">Preferred Contact Method</Label>
              <RadioGroup
                value={formData.preferredContact}
                onValueChange={(value: "email" | "phone") => 
                  setFormData({ ...formData, preferredContact: value })
                }
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="email" id="contact-email" />
                  <Label htmlFor="contact-email" className="cursor-pointer font-normal">Email</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="phone" id="contact-phone" />
                  <Label htmlFor="contact-phone" className="cursor-pointer font-normal">Phone</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Honeypot field - hidden from users, catches bots */}
            <div className="absolute left-[-9999px]" aria-hidden="true">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={formData.honeypot}
                onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
              />
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox 
                id="consent" 
                required 
                checked={consent}
                onCheckedChange={(checked) => setConsent(checked === true)}
              />
              <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
                I confirm this is my email and phone number, and I consent to 4FOX contacting me. 
                See our <Link to="/privacy-policy" className="underline hover:text-primary">Privacy Policy</Link> and{" "}
                <Link to="/data-rights" className="underline hover:text-primary">Data Rights</Link>.
              </Label>
            </div>

            {/* Cloudflare Turnstile */}
            <div className="flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || "1x00000000000000000000AA"}
                onSuccess={setTurnstileToken}
                onExpire={() => setTurnstileToken("")}
                onError={() => setTurnstileToken("")}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isSubmitting || !consent || !turnstileToken}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Message"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <OfficeMap />
    </div>
  );
};

export default ContactForm;
